import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { adapter } from "@/services/db";
import { getSeen, markSeen } from "@/services/crmApi";
import { buildEvents, WATCHED, CATEGORIES, sectionsOf } from "@/lib/notifications";
import { toDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext({
  events: [], unseenBySection: {}, isUnseen: () => false, seeSection: () => {}, seeItems: () => {},
  isRowNew: () => false, seeRows: () => {}, newRowsIn: () => 0,
});
const DEFAULT_SEEN = Date.now() - 3 * 864e5;
// Row-level "seen" baselines are stored next to the section ones, under rows_<section>.
// Visiting a page clears the SIDEBAR dot for that section, but each line item keeps its
// own red dot until it is opened (or "Mark all seen" is pressed on that page).
const ROWS = (sec) => `rows_${sec}`;

export function NotificationsProvider({ children }) {
  const [data, setData] = useState({});
  const [seen, setSeen] = useState({ sections: {}, items: {} });
  const { user } = useAuth();

  useEffect(() => {
    const unsubs = WATCHED.map((c) => adapter.subscribe(c, (rows) => setData((m) => ({ ...m, [c]: rows }))));
    getSeen().then(setSeen).catch(() => {});
    return () => unsubs.forEach((u) => u && u());
  }, []);

  const events = useMemo(() => buildEvents(data), [data]);
  const byKey = useMemo(() => Object.fromEntries(events.map((e) => [e.key, e])), [events]);

  const mine = useCallback((e) => !!(e.by && user?.email && e.by === user.email), [user]);
  const itemSeenAt = useCallback((key) => toDate(seen.items[key.replace(/\./g, "_")])?.getTime() ?? 0, [seen]);
  const secSeenAt = useCallback((k) => toDate(seen.sections[k])?.getTime() ?? DEFAULT_SEEN, [seen]);

  // Sidebar / bell: new since the section was last visited.
  const isUnseen = useCallback((e) => {
    if (mine(e)) return false;
    const s = secSeenAt(CATEGORIES[e.cat].section);
    return e.ts > Math.max(s, itemSeenAt(e.key));
  }, [mine, secSeenAt, itemSeenAt]);

  // Line item: new since this row was opened (or the page's rows were marked seen).
  const isEventRowNew = useCallback((e, section) => {
    if (!e || mine(e)) return false;
    const secs = section ? [section] : sectionsOf(e);
    const base = Math.min(...secs.map((s) => secSeenAt(ROWS(s))));
    return e.ts > Math.max(base, itemSeenAt(e.key));
  }, [mine, secSeenAt, itemSeenAt]);

  const isRowNew = useCallback((coll, id, section) => isEventRowNew(byKey[`${coll}/${id}`], section), [byKey, isEventRowNew]);
  const newRowsIn = useCallback((section, colls) => events.filter((e) => (!colls || colls.includes(e.coll)) && sectionsOf(e).includes(section) && isEventRowNew(e, section)).length, [events, isEventRowNew]);

  const unseenBySection = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      if (!isUnseen(e)) return;
      sectionsOf(e).forEach((s) => { m[s] = (m[s] || 0) + 1; });
    });
    m.notifications = events.filter(isUnseen).length;
    return m;
  }, [events, isUnseen]);

  const stamp = useCallback((sectionKeys, itemKeys) => {
    const now = new Date().toISOString();
    setSeen((s) => ({
      sections: { ...s.sections, ...Object.fromEntries(sectionKeys.map((k) => [k, now])) },
      items: { ...s.items, ...Object.fromEntries(itemKeys.map((k) => [k.replace(/\./g, "_"), now])) },
    }));
    markSeen(sectionKeys, itemKeys).catch(() => {});
  }, []);

  const seeSection = useCallback((sections) => stamp(sections, []), [stamp]);
  const seeItems = useCallback((keys) => stamp([], keys), [stamp]);
  // "Mark all seen" on a page: clears the sidebar dot AND every row dot in it.
  const seeRows = useCallback((sections) => stamp([...sections, ...sections.map(ROWS)], []), [stamp]);

  return (
    <Ctx.Provider value={{ events, data, unseenBySection, isUnseen, seeSection, seeItems, isRowNew, seeRows, newRowsIn }}>
      {children}
    </Ctx.Provider>
  );
}

export const useNotifications = () => useContext(Ctx);
